/**
 * FavoritePlacesManager
 * ----------------------
 * Passenger profile screen listing all favorite places with:
 *   - Reordering (drag native HTML5 up/down buttons for accessibility)
 *   - Renaming inline
 *   - Category change (Home/Work/Airport/Custom)
 *   - Delete
 *   - Smart suggestions row: addresses booked ≥2× that aren't favorites yet
 */
import { useCallback, useEffect, useState } from "react";
import api from "../../lib/api";
import { Home, Briefcase, Plane, Star, ChevronUp, ChevronDown, Trash2, Pencil, Check, X, Plus, Lightbulb, Loader2 } from "lucide-react";
import { Button } from "../ui/button";
import { toast } from "sonner";

const ICON_MAP = { home: Home, work: Briefcase, airport: Plane, custom: Star };
const ICON_LABELS = { home: "Maison", work: "Bureau", airport: "Aéroport", custom: "Favori" };
const ICON_LIST = ["home", "work", "airport", "custom"];

function IconBadge({ icon }) {
  const Ic = ICON_MAP[icon] || Star;
  return <div className="w-9 h-9 rounded-full bg-[#FACC15]/20 flex items-center justify-center shrink-0"><Ic className="w-4 h-4 text-[#0A2540]" /></div>;
}

export default function FavoritePlacesManager() {
  const [places, setPlaces] = useState([]);
  const [suggestions, setSuggestions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingId, setEditingId] = useState(null);
  const [editLabel, setEditLabel] = useState("");
  const [editIcon, setEditIcon] = useState("custom");
  const [saving, setSaving] = useState(false);

  const refresh = useCallback(async () => {
    setLoading(true);
    try {
      const [fp, sg] = await Promise.all([api.get("/me/places"), api.get("/me/places/suggestions").catch(() => ({ data: [] }))]);
      setPlaces(fp.data || []);
      setSuggestions(sg.data || []);
    } finally { setLoading(false); }
  }, []);
  useEffect(() => { refresh(); }, [refresh]);

  const persistOrder = async (next) => {
    setPlaces(next); // optimistic
    try {
      await api.post("/me/places/reorder", { ids: next.map(p => p.id) });
    } catch (_e) {
      toast.error("Impossible de réordonner");
      refresh();
    }
  };

  const move = (idx, dir) => {
    const j = idx + dir;
    if (j < 0 || j >= places.length) return;
    const next = [...places];
    [next[idx], next[j]] = [next[j], next[idx]];
    persistOrder(next);
  };

  const startEdit = (fp) => {
    setEditingId(fp.id);
    setEditLabel(fp.label || "");
    setEditIcon(fp.icon || "custom");
  };

  const saveEdit = async () => {
    if (!editLabel.trim()) { toast.error("Nom requis"); return; }
    setSaving(true);
    try {
      await api.patch(`/me/places/${editingId}`, { label: editLabel.trim(), icon: editIcon });
      setEditingId(null);
      refresh();
      toast.success("Favori mis à jour");
    } catch (e) { toast.error(e?.response?.data?.detail || "Erreur"); }
    finally { setSaving(false); }
  };

  const remove = async (id) => {
    try {
      await api.delete(`/me/places/${id}`);
      refresh();
      toast.success("Favori supprimé");
    } catch (_e) { toast.error("Erreur"); }
  };

  const acceptSuggestion = async (sg, icon = "custom") => {
    try {
      await api.post("/me/places", {
        label: sg.address.split(",")[0].trim().slice(0, 60) || "Lieu fréquent",
        address: sg.address, lat: sg.lat, lng: sg.lng, icon,
      });
      toast.success("Lieu ajouté aux favoris");
      refresh();
    } catch (e) { toast.error(e?.response?.data?.detail || "Erreur"); }
  };

  return (
    <div className="space-y-6" data-testid="favorite-places-manager">
      <header>
        <h2 className="text-lg font-bold text-[#0A2540]" style={{ fontFamily: "Outfit" }}>Mes lieux favoris</h2>
        <p className="text-xs text-[#64748B]">Sauvegardez maison, bureau et aéroport pour prefill vos courses en un tap. Réordonnez-les selon leur fréquence.</p>
      </header>

      {/* Smart suggestions */}
      {suggestions.length > 0 && (
        <section className="rounded-2xl border border-[#FACC15]/40 bg-[#FACC15]/10 p-4" data-testid="places-suggestions">
          <div className="flex items-center gap-2 mb-2">
            <Lightbulb className="w-4 h-4 text-[#0A2540]" />
            <h3 className="text-sm font-bold text-[#0A2540]">Suggestions intelligentes</h3>
          </div>
          <p className="text-[11px] text-[#64748B] mb-3">Vous êtes déjà passé plusieurs fois à ces adresses — sauvegardez-les pour prefill instantané.</p>
          <div className="space-y-2">
            {suggestions.map((sg) => (
              <div key={`${sg.lat}-${sg.lng}`} className="flex items-center gap-3 rounded-xl bg-white border border-[#FACC15]/50 px-3 py-2" data-testid={`suggestion-${sg.lat}-${sg.lng}`}>
                <span className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-[#0A2540] text-white shrink-0" data-testid={`suggestion-count-${sg.lat}-${sg.lng}`}>×{sg.count}</span>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-semibold text-[#0A2540] truncate">{sg.address}</p>
                  <p className="text-[10px] text-[#64748B]">{sg.lat.toFixed(4)}, {sg.lng.toFixed(4)}</p>
                </div>
                <div className="flex gap-1 shrink-0">
                  {ICON_LIST.map((ic) => {
                    const Ic = ICON_MAP[ic];
                    return (
                      <button
                        key={ic}
                        type="button"
                        onClick={() => acceptSuggestion(sg, ic)}
                        title={`Enregistrer comme ${ICON_LABELS[ic]}`}
                        className="w-7 h-7 rounded-full bg-[#0A2540]/5 hover:bg-[#0A2540] hover:text-white text-[#0A2540] flex items-center justify-center transition"
                        data-testid={`suggestion-save-${ic}-${sg.lat}-${sg.lng}`}
                      >
                        <Ic className="w-3.5 h-3.5" />
                      </button>
                    );
                  })}
                </div>
              </div>
            ))}
          </div>
        </section>
      )}

      {/* Places list */}
      <section>
        {loading ? (
          <div className="py-10 text-center text-[#64748B]"><Loader2 className="w-5 h-5 animate-spin mx-auto" /></div>
        ) : places.length === 0 ? (
          <div className="rounded-2xl border border-dashed border-[#CBD5E1] p-8 text-center" data-testid="places-empty-state">
            <Star className="w-8 h-8 text-[#94A3B8] mx-auto mb-2" />
            <p className="text-sm text-[#64748B]">Aucun lieu favori pour le moment.</p>
            <p className="text-[11px] text-[#94A3B8] mt-1">Enregistrez-en depuis n'importe quel écran de réservation (bouton “Enregistrer”).</p>
          </div>
        ) : (
          <ul className="space-y-2">
            {places.map((fp, idx) => {
              const isEditing = editingId === fp.id;
              return (
                <li key={fp.id} className="flex items-center gap-2 rounded-2xl border border-[#CBD5E1] bg-white p-3" data-testid={`place-row-${fp.id}`}>
                  <div className="flex flex-col shrink-0">
                    <button type="button" onClick={() => move(idx, -1)} disabled={idx === 0} className="w-6 h-5 rounded-t bg-[#F1F5F9] disabled:opacity-40 hover:bg-[#0A2540]/10 flex items-center justify-center" data-testid={`place-move-up-${fp.id}`}><ChevronUp className="w-3 h-3" /></button>
                    <button type="button" onClick={() => move(idx, +1)} disabled={idx === places.length - 1} className="w-6 h-5 rounded-b bg-[#F1F5F9] disabled:opacity-40 hover:bg-[#0A2540]/10 flex items-center justify-center" data-testid={`place-move-down-${fp.id}`}><ChevronDown className="w-3 h-3" /></button>
                  </div>
                  <IconBadge icon={isEditing ? editIcon : fp.icon} />
                  <div className="flex-1 min-w-0">
                    {isEditing ? (
                      <div className="space-y-1.5">
                        <input
                          type="text"
                          value={editLabel}
                          onChange={(e) => setEditLabel(e.target.value)}
                          maxLength={60}
                          className="w-full rounded-md border border-[#CBD5E1] px-2 py-1 text-sm outline-none focus:border-[#0A2540]"
                          data-testid={`place-edit-label-${fp.id}`}
                          autoFocus
                        />
                        <div className="flex flex-wrap gap-1">
                          {ICON_LIST.map((ic) => {
                            const Ic = ICON_MAP[ic];
                            return (
                              <button
                                key={ic}
                                type="button"
                                onClick={() => setEditIcon(ic)}
                                className={`inline-flex items-center gap-1 text-[10px] font-semibold px-2 py-1 rounded-full transition ${editIcon === ic ? "bg-[#0A2540] text-white" : "bg-white text-[#0A2540] border border-[#CBD5E1] hover:border-[#0A2540]"}`}
                                data-testid={`place-edit-icon-${ic}-${fp.id}`}
                              >
                                <Ic className="w-3 h-3" /> {ICON_LABELS[ic]}
                              </button>
                            );
                          })}
                        </div>
                        <p className="text-[10px] text-[#64748B] truncate">{fp.address}</p>
                      </div>
                    ) : (
                      <>
                        <p className="text-sm font-semibold text-[#0A2540] flex items-center gap-1.5">{fp.label}
                          <span className="text-[9px] uppercase tracking-wider text-[#64748B] font-bold">{ICON_LABELS[fp.icon] || "Favori"}</span>
                        </p>
                        <p className="text-[11px] text-[#64748B] truncate">{fp.address}</p>
                        <p className="text-[10px] text-[#94A3B8]">{fp.lat.toFixed(4)}, {fp.lng.toFixed(4)}</p>
                      </>
                    )}
                  </div>
                  <div className="flex gap-1 shrink-0">
                    {isEditing ? (
                      <>
                        <Button size="sm" onClick={saveEdit} disabled={saving} className="h-8 bg-[#10B981] hover:bg-[#059669] text-white" data-testid={`place-save-${fp.id}`}>
                          {saving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Check className="w-3.5 h-3.5" />}
                        </Button>
                        <Button size="sm" variant="outline" onClick={() => setEditingId(null)} className="h-8 border-[#CBD5E1]" data-testid={`place-cancel-${fp.id}`}>
                          <X className="w-3.5 h-3.5" />
                        </Button>
                      </>
                    ) : (
                      <>
                        <Button size="sm" variant="outline" onClick={() => startEdit(fp)} className="h-8 border-[#CBD5E1]" data-testid={`place-edit-${fp.id}`}>
                          <Pencil className="w-3.5 h-3.5" />
                        </Button>
                        <Button size="sm" variant="outline" onClick={() => remove(fp.id)} className="h-8 border-[#EF4444]/30 text-[#EF4444] hover:bg-[#EF4444]/10" data-testid={`place-delete-${fp.id}`}>
                          <Trash2 className="w-3.5 h-3.5" />
                        </Button>
                      </>
                    )}
                  </div>
                </li>
              );
            })}
          </ul>
        )}
        {places.length > 0 && (
          <p className="text-[10px] text-[#94A3B8] mt-2 flex items-center gap-1"><Plus className="w-3 h-3" /> {places.length}/10 lieux enregistrés</p>
        )}
      </section>
    </div>
  );
}
